import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { ViewMode } from '../Wizard';
import { Button, BottomNav, ProgressBar } from '../ui';
import { Tabs } from '../components/Tabs';
import { TextInput } from '../components/TextInput';
import { EntriModal } from '../components/EntriModal';
import { GoogleSignInModal } from '../components/GoogleSignInModal';
import { LoadingScreen } from '../components/LoadingScreen';
import { Celebration } from '../components/Celebration';
import { CheckIcon } from '../icons/CheckIcon';
import { ChatView } from './ChatView';

type Step2Tab = 'automatic' | 'manual';

type SubStep = {
  title: string;
  description: ReactNode;
};

type StepDef = {
  number: number;
  title: string;
  description: string;
  videoUrl?: string;
  posterUrl?: string;
  substeps: SubStep[];
};

const STEPS: StepDef[] = [
  {
    number: 1,
    title: 'Open Google Postmaster Tools',
    description: 'Register your domain in Google',
    videoUrl: '/step1/video.mov',
    posterUrl: '/step1/1.gif',
    substeps: [
      {
        title: 'Google Postmaster Tools',
        description: (
          <>
            Go to{' '}
            <a
              className="underline"
              href="https://postmaster.google.com"
              target="_blank"
              rel="noreferrer"
            >
              Google Postmaster Tools
            </a>{' '}
            and sign in with your Google account.
          </>
        ),
      },
      {
        title: 'Add domain',
        description: 'Click Add domain and enter your sending domain (e.g. yourbrand.com).',
      },
      {
        title: 'TXT record',
        description:
          "Google will generate a TXT record. Copy it — you'll need it in the next step.",
      },
    ],
  },
  {
    number: 2,
    title: 'Add the TXT record to your DNS',
    description: 'Connect your domain provider so Google can verify it',
    substeps: [
      {
        title: 'Domain provider',
        description: 'Log in to your domain provider (Squarespace, GoDaddy, etc.).',
      },
      {
        title: 'DNS settings',
        description: 'Open your DNS settings and create a new TXT record there.',
      },
      {
        title: 'Save record',
        description: 'Save the record — changes can take a few minutes to propagate.',
      },
    ],
  },
  {
    number: 3,
    title: 'Verify your domain in Google Postmaster',
    description: 'Confirm the TXT record from inside Google',
    substeps: [
      {
        title: 'Google Postmaster Tools',
        description: 'Head back to Google Postmaster Tools where you started.',
      },
      {
        title: 'Verify and check',
        description: 'Find your domain and click Verify — the check is instant.',
      },
      {
        title: 'Continue the process',
        description: 'Once you see the Verified badge, come back and click Continue.',
      },
    ],
  },
  {
    number: 4,
    title: 'Connect Flodesk with Google',
    description: 'Authorize Flodesk to read your reputation data',
    substeps: [
      {
        title: 'Authorize with Google',
        description:
          'Click Authorize with Google and pick the account you used for Postmaster Tools.',
      },
      {
        title: 'Permissions',
        description: 'Review the permissions and click Continue — we only read reputation data.',
      },
    ],
  },
  {
    number: 5,
    title: 'All set!',
    description: 'Your domain is connected to Google Postmaster',
    substeps: [],
  },
];

const readStepFromUrl = () => {
  if (typeof window === 'undefined') return 1;
  const stepParam = new URLSearchParams(window.location.search).get('step');
  const step = stepParam ? parseInt(stepParam, 10) : NaN;
  return step >= 1 && step <= STEPS.length ? step : 1;
};

export function StepsScreen({
  onComplete,
  onBackToIntro,
  viewMode = 'normal',
  onExitMode,
}: {
  onComplete: () => void;
  onBackToIntro: () => void;
  viewMode?: ViewMode;
  onExitMode?: () => void;
}) {
  const helpMode = viewMode === 'help';
  const cascadeMode = viewMode === 'cascade';
  const compactMode = viewMode === 'compact';
  const chatMode = viewMode === 'chat';
  const [activeStep, setActiveStep] = useState(readStepFromUrl);
  const [step2Tab, setStep2Tab] = useState<Step2Tab>('automatic');
  const [step2Value, setStep2Value] = useState('');
  const [step2Connected, setStep2Connected] = useState(false);
  const [step4Connected, setStep4Connected] = useState(false);
  const [step4Email] = useState('alejandro@flodesk.com');
  const [entriModalOpen, setEntriModalOpen] = useState(false);
  const [googleModalOpen, setGoogleModalOpen] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const hasReplacedInitialUrl = useRef(false);

  useEffect(() => {
    const handlePopState = () => setActiveStep(readStepFromUrl());
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('step') === String(activeStep)) {
      hasReplacedInitialUrl.current = true;
      return;
    }
    url.searchParams.set('step', String(activeStep));
    if (!hasReplacedInitialUrl.current) {
      window.history.replaceState(null, '', url);
      hasReplacedInitialUrl.current = true;
    } else {
      window.history.pushState(null, '', url);
    }
  }, [activeStep]);

  const advance = () => {
    if (activeStep < STEPS.length) {
      setActiveStep(activeStep + 1);
    } else {
      onComplete();
    }
  };

  const cascadeContainerRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  const [visibleSection, setVisibleSection] = useState(1);

  const scrollToSection = (n: number) => {
    const el = sectionRefs.current[n - 1];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useLayoutEffect(() => {
    if (!cascadeMode) return undefined;
    const container = cascadeContainerRef.current;
    if (!container) return undefined;
    const observer = new IntersectionObserver(
      entries => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) {
          const idx = sectionRefs.current.findIndex(el => el === visible.target);
          if (idx !== -1) setVisibleSection(idx + 1);
        }
      },
      { root: container, threshold: [0.25, 0.5, 0.75, 1] },
    );
    sectionRefs.current.forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, [cascadeMode]);

  const handleContinue = () => {
    if (cascadeMode) {
      if (visibleSection < STEPS.length) scrollToSection(visibleSection + 1);
      else onComplete();
      return;
    }
    if (activeStep === 2 && !step2Connected) {
      setEntriModalOpen(true);
    } else if (activeStep === 4 && !step4Connected) {
      setGoogleModalOpen(true);
    } else {
      advance();
    }
  };

  const handleBack = () => {
    if (cascadeMode) {
      if (visibleSection > 1) scrollToSection(visibleSection - 1);
      else onBackToIntro();
      return;
    }
    if (activeStep > 1) {
      setActiveStep(activeStep - 1);
    } else {
      onBackToIntro();
    }
  };

  const handleEntriContinue = () => {
    setEntriModalOpen(false);
    setStep2Connected(true);
    advance();
  };

  const handleGoogleContinue = () => {
    setGoogleModalOpen(false);
    setTransitioning(true);
    setTimeout(() => {
      setTransitioning(false);
      setStep4Connected(true);
      advance();
    }, 2200);
  };

  const activeStepDef = STEPS[activeStep - 1];
  const progress = (activeStep / STEPS.length) * 100;
  const isCelebration = activeStep === STEPS.length;

  let continueLabel = 'Continue';
  let continueDisabled = false;
  if (activeStep === 2 && !step2Connected && step2Tab === 'automatic') {
    continueDisabled = step2Value.trim().length === 0;
  }
  if (activeStep === 4 && !step4Connected) {
    continueLabel = 'Authorize with Google';
  }
  if (isCelebration) {
    continueLabel = 'Finish';
  }

  const contentBody = isCelebration ? (
    <Celebration />
  ) : (
    <>
      <StepMedia videoUrl={activeStepDef.videoUrl} posterUrl={activeStepDef.posterUrl} />

      {activeStep === 2 && !step2Connected ? (
        <Step2Body
          tab={step2Tab}
          onTabChange={setStep2Tab}
          value={step2Value}
          onValueChange={setStep2Value}
          substeps={activeStepDef.substeps}
          circleSize={helpMode ? 38 : 32}
        />
      ) : activeStep === 2 && step2Connected ? (
        <ConnectedCard
          label="Connected via Entri"
          detail="flodesk.com"
          onReconnect={() => {
            setStep2Connected(false);
            setStep2Value('');
          }}
        />
      ) : activeStep === 4 && step4Connected ? (
        <ConnectedCard
          label="Connected to Google"
          detail={step4Email}
          onReconnect={() => setStep4Connected(false)}
        />
      ) : (
        <SubStepList substeps={activeStepDef.substeps} circleSize={helpMode ? 38 : 32} />
      )}
    </>
  );

  if (chatMode) {
    return <ChatView onExit={onExitMode} onComplete={onExitMode} />;
  }

  if (compactMode) {
    return (
      <CompactView
        steps={STEPS}
        activeStep={activeStep}
        step2Tab={step2Tab}
        step2Value={step2Value}
        onExit={onExitMode}
      />
    );
  }

  if (cascadeMode) {
    return (
      <main className="absolute inset-0">
        <div className="relative h-full">
          <div
            ref={cascadeContainerRef}
            className="absolute inset-0 overflow-y-auto snap-y snap-mandatory scroll-smooth"
          >
            <div className="mx-auto flex w-[800px] flex-col px-l2">
              {STEPS.map((step, i) => {
                const isActive = i + 1 === visibleSection;
                return (
                  <section
                    key={step.number}
                    ref={el => {
                      sectionRefs.current[i] = el;
                    }}
                    className="flex min-h-full snap-center snap-always flex-col items-center justify-center gap-[40px] py-[60px] transition-[opacity,transform,filter] duration-500 ease-out"
                    style={{
                      opacity: isActive ? 1 : 0.18,
                      transform: `scale(${isActive ? 1 : 0.94})`,
                      filter: isActive ? 'blur(0px)' : 'blur(2px)',
                    }}
                  >
                    {step.number !== STEPS.length && (
                      <h2 className="w-[480px] text-center text-[22px] font-medium leading-[28px] text-content">
                        {step.title}
                      </h2>
                    )}
                    {step.number === STEPS.length ? (
                      <Celebration />
                    ) : step.number === 2 ? (
                      <>
                        <StepMedia videoUrl={step.videoUrl} posterUrl={step.posterUrl} />
                        <Step2Body
                          tab={step2Tab}
                          onTabChange={setStep2Tab}
                          value={step2Value}
                          onValueChange={setStep2Value}
                          substeps={step.substeps}
                          circleSize={38}
                        />
                      </>
                    ) : step.number === 1 ? (
                      <>
                        <StepMedia videoUrl={step.videoUrl} posterUrl={step.posterUrl} />
                        <p className="w-[400px] text-center text-[14px] font-normal leading-[22px] text-content-muted">
                          Sign in to{' '}
                          <a
                            className="underline"
                            href="https://postmaster.google.com"
                            target="_blank"
                            rel="noreferrer"
                          >
                            Google Postmaster Tools
                          </a>
                          , add your sending domain, and copy the TXT verification record generated
                          by Google for the next step.
                        </p>
                      </>
                    ) : (
                      <>
                        <StepMedia videoUrl={step.videoUrl} posterUrl={step.posterUrl} />
                        <SubStepList substeps={step.substeps} circleSize={38} />
                      </>
                    )}
                  </section>
                );
              })}
            </div>
          </div>
          <SideRail total={STEPS.length} active={visibleSection} onJump={scrollToSection} />
          <div className="pointer-events-none absolute bottom-0 left-0 right-0">
            <div className="pointer-events-auto">
              <HelpModeBottom onExitHelp={onExitMode} />
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (helpMode) {
    return (
      <main className="absolute inset-0 flex flex-col">
        <TopNav
          onBack={handleBack}
          onContinue={handleContinue}
          continueLabel={continueLabel}
          continueDisabled={continueDisabled}
        />
        <div className="flex flex-1 items-center justify-center px-l2">
          <div
            key={`step-help-${activeStep}`}
            className="flex w-[800px] flex-col items-center gap-[40px]"
            style={{ animation: 'wizard-fadein 200ms ease-out' }}
          >
            <StepDots total={STEPS.length} active={activeStep} />
            <h1 className="w-[480px] text-center text-[22px] font-medium leading-[28px] text-content">
              {activeStepDef.title}
            </h1>
            {contentBody}
          </div>
        </div>
        <HelpModeBottom onExitHelp={onExitMode} />

        <EntriModal
          open={entriModalOpen}
          onClose={() => setEntriModalOpen(false)}
          onContinue={handleEntriContinue}
        />
        <GoogleSignInModal
          open={googleModalOpen}
          onClose={() => setGoogleModalOpen(false)}
          onContinue={handleGoogleContinue}
        />
        {transitioning && <LoadingScreen />}
      </main>
    );
  }

  return (
    <main className="absolute inset-0 top-[88px] bottom-0 flex flex-col">
      <div className="flex flex-1 items-center justify-center px-l2">
        <div
          key={`step-${activeStep}`}
          className="flex w-[800px] flex-col items-center gap-[40px]"
          style={{ animation: 'wizard-fadein 200ms ease-out' }}
        >
          <Title title={activeStepDef.title} description={activeStepDef.description} />
          {contentBody}
        </div>
      </div>

      <BottomNav
        onBack={handleBack}
        onContinue={handleContinue}
        continueLabel={continueLabel}
        continueDisabled={continueDisabled}
      />
      <ProgressBar value={progress} />

      <EntriModal
        open={entriModalOpen}
        onClose={() => setEntriModalOpen(false)}
        onContinue={handleEntriContinue}
      />
      <GoogleSignInModal
        open={googleModalOpen}
        onClose={() => setGoogleModalOpen(false)}
        onContinue={handleGoogleContinue}
      />
      {transitioning && <LoadingScreen />}
    </main>
  );
}

function TopNav({
  onBack,
  onContinue,
  continueLabel,
  continueDisabled,
}: {
  onBack: () => void;
  onContinue: () => void;
  continueLabel: string;
  continueDisabled?: boolean;
}) {
  return (
    <div className="flex h-[88px] items-center justify-between px-l2">
      <button
        type="button"
        onClick={onBack}
        className="rounded-[6px] px-s py-1 text-[14px] font-normal leading-[20px] text-content-muted transition-colors hover:text-content focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-shade-12"
      >
        {'< Back'}
      </button>
      <Button variant="outline" disabled={continueDisabled} onClick={onContinue}>
        {continueLabel}
      </Button>
    </div>
  );
}

function HelpModeBottom({ onExitHelp }: { onExitHelp?: () => void }) {
  return (
    <div className="flex h-[88px] items-center justify-end px-l2">
      <button
        type="button"
        onClick={onExitHelp}
        className="text-[14px] font-normal leading-[20px] text-content-muted underline transition-colors hover:text-content focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-shade-12"
      >
        Need help?
      </button>
    </div>
  );
}

function SideRail({
  total,
  active,
  onJump,
}: {
  total: number;
  active: number;
  onJump: (n: number) => void;
}) {
  return (
    <div className="pointer-events-none absolute right-l2 top-1/2 z-10 flex -translate-y-1/2 flex-col gap-[10px]">
      {Array.from({ length: total }).map((_, i) => {
        const isActive = i + 1 === active;
        return (
          <button
            key={i}
            type="button"
            aria-label={`Go to step ${i + 1}`}
            onClick={() => onJump(i + 1)}
            className={`pointer-events-auto w-[28px] rounded-full transition-all duration-200 ${
              isActive
                ? 'h-[3px] bg-content'
                : 'h-[2px] bg-[rgba(60,50,47,0.28)] hover:bg-[rgba(60,50,47,0.5)]'
            }`}
          />
        );
      })}
    </div>
  );
}

type CompactViewProps = {
  steps: StepDef[];
  activeStep: number;
  step2Tab: Step2Tab;
  step2Value: string;
  onExit?: () => void;
};

const CompactView = ({ steps, activeStep, step2Tab, step2Value, onExit }: CompactViewProps) => {
  const step = steps[activeStep - 1];

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onExit?.();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onExit]);

  const substepRows = (() => {
    if (activeStep === 2 && step2Tab === 'automatic') {
      return [
        { content: <>Paste the TXT record from Google Postmaster.</> },
        {
          content: (
            <span className="text-content-muted">
              {step2Value || 'google-site-verification=xxxxxxxxxxxxxxxx'}
            </span>
          ),
        },
        { content: <>Click Continue — Entri will detect your DNS and add the record.</> },
      ];
    }
    return step.substeps.map(s => ({ content: s.description }));
  })();

  return (
    <main className="absolute inset-0 flex items-center justify-center bg-white">
      <button
        type="button"
        aria-label="Close compact view"
        onClick={onExit}
        className="absolute inset-0 cursor-default"
      />
      <div
        className="relative flex w-[458px] flex-col items-center gap-[40px]"
        onClick={e => e.stopPropagation()}
        role="presentation"
      >
        <StepDots total={steps.length} active={activeStep} />
        <h1 className="w-full text-center text-[24px] font-medium leading-[28.6px] text-content">
          {step.title}
        </h1>
        {activeStep < steps.length && (
          <CompactMedia videoUrl={step.videoUrl} posterUrl={step.posterUrl} />
        )}
        {substepRows.length > 0 && (
          <div className="flex w-full flex-col gap-s">
            {substepRows.map((row, i) => (
              <div
                key={i}
                className="flex items-center gap-s rounded-[8px] border border-[rgba(60,50,47,0.12)] px-[12px] py-s"
              >
                <span className="size-[16px] shrink-0 rounded-full border border-[rgba(60,50,47,0.12)]" />
                <p className="flex-1 text-[12px] font-normal leading-[17.8px] text-content">
                  {row.content}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

const CompactMedia = ({ videoUrl, posterUrl }: { videoUrl?: string; posterUrl?: string }) => {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const handlePlay = () => {
    if (!videoUrl) return;
    setPlaying(true);
    requestAnimationFrame(() => {
      videoRef.current?.play().catch(() => {});
    });
  };

  return (
    <div
      className="relative w-full overflow-hidden rounded-[10px] bg-[#E2E2E2] shadow-[0_1px_1px_-0.1px_rgba(0,0,0,0.05),0_1.98px_3.06px_-0.36px_rgba(0,0,0,0.05)]"
      style={{ aspectRatio: '420 / 231' }}
    >
      {videoUrl && playing ? (
        <video
          ref={videoRef}
          src={videoUrl}
          controls
          playsInline
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : posterUrl ? (
        <img src={posterUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(135deg, rgba(60,50,47,0.15) 0%, rgba(120,120,120,0.18) 50%, rgba(255,255,255,0.35) 100%)',
          }}
        />
      )}
      {videoUrl && !playing && (
        <button
          type="button"
          aria-label="Play video"
          onClick={handlePlay}
          className="absolute bottom-[18px] right-[18px] flex size-[32px] items-center justify-center rounded-full bg-[rgba(57,48,45,0.52)] text-white transition-colors hover:bg-[rgba(57,48,45,0.68)]"
        >
          <svg width="8" height="10" viewBox="0 0 8 10" aria-hidden="true" className="ml-[1px]">
            <path d="M0.555 0.555L7.778 5L0.555 9.445V0.555Z" fill="currentColor" />
          </svg>
        </button>
      )}
    </div>
  );
};

function StepDots({ total, active }: { total: number; active: number }) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className={`size-2 rounded-full transition-colors ${
            i + 1 === active ? 'bg-content' : 'bg-[rgba(60,50,47,0.18)]'
          }`}
        />
      ))}
    </div>
  );
}

function Title({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex w-[480px] flex-col items-center gap-m text-center">
      <h1 className="text-[22px] font-medium leading-[28px] text-content">{title}</h1>
      <p className="w-[418px] text-[14px] font-normal leading-[20px] text-content-muted">
        {description}
      </p>
    </div>
  );
}

function StepMedia({ videoUrl, posterUrl }: { videoUrl?: string; posterUrl?: string }) {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const handlePlay = () => {
    if (!videoUrl) return;
    setPlaying(true);
    requestAnimationFrame(() => {
      videoRef.current?.play().catch(() => {});
    });
  };

  return (
    <div className="relative h-[450px] w-full overflow-hidden rounded-[10px] bg-[#E2E2E2] shadow-[0_1px_1px_-0.1px_rgba(0,0,0,0.05),0_1.98px_3.06px_-0.36px_rgba(0,0,0,0.05),0_5.881px_14.045px_-1.944px_rgba(0,0,0,0.09)]">
      {videoUrl && playing ? (
        <video
          ref={videoRef}
          src={videoUrl}
          controls
          playsInline
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : posterUrl ? (
        <img src={posterUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(135deg, rgba(60,50,47,0.15) 0%, rgba(120,120,120,0.18) 50%, rgba(255,255,255,0.35) 100%)',
          }}
        />
      )}

      {videoUrl && !playing && (
        <button
          type="button"
          aria-label="Play video"
          onClick={handlePlay}
          className="absolute bottom-[18px] right-[18px] flex size-[32px] items-center justify-center rounded-full bg-[rgba(57,48,45,0.52)] text-white transition-colors hover:bg-[rgba(57,48,45,0.68)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        >
          <svg
            width="8"
            height="10"
            viewBox="0 0 8 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            className="ml-[1px]"
          >
            <path d="M0.555 0.555L7.778 5L0.555 9.445V0.555Z" fill="currentColor" />
          </svg>
        </button>
      )}
    </div>
  );
}

function SubStepList({ substeps, circleSize = 32 }: { substeps: SubStep[]; circleSize?: number }) {
  if (substeps.length === 0) return null;
  return (
    <div className="flex w-full flex-col gap-3">
      {substeps.map((sub, i) => (
        <div key={i} className="flex items-center gap-3">
          <div
            className="flex shrink-0 items-center justify-center rounded-full bg-[#f3f2f1] text-[14px] font-normal leading-[20px] text-content"
            style={{ width: circleSize, height: circleSize }}
          >
            {i + 1}
          </div>
          <div className="flex flex-1 flex-col">
            <p className="text-[14px] font-medium leading-[20px] text-content">{sub.title}</p>
            <p className="text-[12px] font-normal leading-[17.8px] text-content-muted">
              {sub.description}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

function ConnectedCard({
  label,
  detail,
  onReconnect,
}: {
  label: string;
  detail: string;
  onReconnect: () => void;
}) {
  return (
    <div className="flex w-full items-start justify-between gap-3 rounded-[10px] bg-[#f9f8f8] p-l">
      <div className="flex items-start gap-3">
        <CheckIcon className="mt-[3px] size-3 shrink-0 text-content" />
        <div className="flex flex-col">
          <p className="text-[14px] font-medium leading-[20px] text-content">{label}</p>
          <p className="text-[12px] font-normal leading-[17.8px] text-content-muted">{detail}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={onReconnect}
        className="shrink-0 text-[12px] font-normal leading-[17.8px] text-content-muted underline transition-colors hover:text-content"
      >
        Reconnect
      </button>
    </div>
  );
}

function Step2Body({
  tab,
  onTabChange,
  value,
  onValueChange,
  substeps,
  circleSize = 32,
}: {
  tab: Step2Tab;
  onTabChange: (tab: Step2Tab) => void;
  value: string;
  onValueChange: (v: string) => void;
  substeps: SubStep[];
  circleSize?: number;
}) {
  return (
    <div className="flex w-full flex-col gap-l">
      <Tabs
        tabs={[
          { id: 'automatic', label: 'Automatic' },
          { id: 'manual', label: 'Manual' },
        ]}
        active={tab}
        onChange={id => onTabChange(id as Step2Tab)}
      />
      {tab === 'manual' ? (
        <SubStepList substeps={substeps} circleSize={circleSize} />
      ) : (
        <div className="flex w-full flex-col gap-m">
          <div className="flex items-start gap-3">
            <div
              className="flex shrink-0 items-center justify-center rounded-full bg-[#f3f2f1] text-[14px] font-normal leading-[20px] text-content"
              style={{ width: circleSize, height: circleSize }}
            >
              1
            </div>
            <div className="flex flex-1 flex-col gap-s">
              <div className="flex flex-col">
                <p className="text-[14px] font-medium leading-[20px] text-content">TXT record</p>
                <p className="text-[12px] font-normal leading-[17.8px] text-content-muted">
                  Paste the TXT record from Google Postmaster.
                </p>
              </div>
              <TextInput
                placeholder="google-site-verification=xxxxxxxxxxxxxxxx"
                value={value}
                onChange={e => onValueChange(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div
              className="flex shrink-0 items-center justify-center rounded-full bg-[#f3f2f1] text-[14px] font-normal leading-[20px] text-content"
              style={{ width: circleSize, height: circleSize }}
            >
              2
            </div>
            <div className="flex flex-1 flex-col">
              <p className="text-[14px] font-medium leading-[20px] text-content">Continue</p>
              <p className="text-[12px] font-normal leading-[17.8px] text-content-muted">
                Click Continue — Entri will detect your DNS provider and add the record
                automatically.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Kept to avoid TS unused warnings if referenced elsewhere
export type { Step2Tab };
export { Button };
